import { useCallback, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import FileInput from "../common/FileInput";
import { TEXT_SMALL, TEXT_TITLE } from "../../style";

export default function DialogAddNotification({
    visible,
    closeDialog,
    branches = [],
    notificationTypes = [],
    requestAPI,
    showToast,
    onSent,
}) {
    const [form, setForm] = useState({ batch_ids: [], attachments: [] });
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pendingAttachment, setPendingAttachment] = useState();

    useEffect(() => {
        if (!visible) {
            setForm({ batch_ids: [], attachments: [] });
            setBatches([]);
            setPendingAttachment();
        }
    }, [visible]);

    useEffect(() => {
        if (!form.branch_id) {
            setBatches([]);
            return;
        }
        requestAPI({
            requestPath: `batch-events/batches?branch_id=${form.branch_id}`,
            requestMethod: "GET",
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setBatches(data);
                }
            },
        });
    }, [form.branch_id, requestAPI]);

    const addPendingAttachment = useCallback(() => {
        if (!pendingAttachment) {
            return;
        }
        setForm((prev) => ({
            ...prev,
            attachments: [...(prev.attachments || []), pendingAttachment],
        }));
        setPendingAttachment();
    }, [pendingAttachment]);

    const save = useCallback(() => {
        if (!form.title?.trim() || !form.type_id || !form.batch_ids?.length) {
            showToast({ severity: "warn", summary: "Required", detail: "Title, type and batches are required", life: 2000 });
            return;
        }

        requestAPI({
            requestPath: "notifications/",
            requestMethod: "POST",
            requestPostBody: {
                title: form.title.trim(),
                description: form.description?.trim() || null,
                type_id: form.type_id,
                batch_ids: form.batch_ids,
                attachments: form.attachments || [],
            },
            setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed to send notification", life: 2000 }),
            onResponseReceieved: (result, responseCode) => {
                if (result && responseCode === 201) {
                    showToast({
                        severity: "success",
                        summary: "Sent",
                        detail: `Notification sent to ${result.notified ?? 0} users`,
                        life: 1500,
                    });
                    onSent?.(result);
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: result?.error || "Failed to send notification", life: 2000 });
                }
            },
        });
    }, [closeDialog, form, onSent, requestAPI, showToast]);

    return (
        <Dialog
            header="Send Notification"
            visible={visible}
            className="w-11 max-h-screen"
            onHide={closeDialog}
            pt={{ headertitle: { className: TEXT_TITLE }, content: { className: "overflow-y-auto" } }}
        >
            <FloatLabel className="mt-4">
                <InputText
                    className="w-full"
                    value={form.title || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Title</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputTextarea
                    className="w-full"
                    rows={3}
                    value={form.description || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Description</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    options={notificationTypes}
                    optionLabel="title"
                    optionValue="id"
                    value={form.type_id}
                    itemTemplate={(option) => (
                        <div className="flex align-items-center gap-2">
                            <span className="border-circle w-1rem h-1rem" style={{ backgroundColor: option.color }} />
                            <span>{option.title}</span>
                        </div>
                    )}
                    valueTemplate={(option, props) => {
                        const selected = option || notificationTypes.find((type) => type.id === form.type_id);
                        return selected ? (
                            <div className="flex align-items-center gap-2">
                                <span className="border-circle w-1rem h-1rem" style={{ backgroundColor: selected.color }} />
                                <span>{selected.title}</span>
                            </div>
                        ) : (
                            <span>{props.placeholder || "\u00A0"}</span>
                        );
                    }}
                    onChange={(e) => setForm((prev) => ({ ...prev, type_id: e.value }))}
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Type</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    options={branches}
                    optionLabel="title"
                    optionValue="id"
                    value={form.branch_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, branch_id: e.value, batch_ids: [] }))}
                    disabled={loading}
                />
                <label className={TEXT_SMALL}>Branch</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <MultiSelect
                    className="w-full"
                    options={batches}
                    optionLabel="title"
                    optionValue="id"
                    value={form.batch_ids}
                    onChange={(e) => setForm((prev) => ({ ...prev, batch_ids: e.value }))}
                    disabled={loading || !form.branch_id}
                    display="chip"
                />
                <label className={TEXT_SMALL}>Batches</label>
            </FloatLabel>

            <FileInput
                className="mt-4"
                label="Attachment"
                type="image"
                cdn_url={pendingAttachment}
                setCDNUrl={setPendingAttachment}
                disabled={loading}
            />
            <div className="flex align-items-center gap-2 mt-2">
                <Button
                    label="Add Attachment"
                    icon="pi pi-plus"
                    severity="secondary"
                    outlined
                    disabled={loading || !pendingAttachment}
                    onClick={addPendingAttachment}
                />
                <span className={TEXT_SMALL}>{form.attachments?.length || 0} attached</span>
            </div>
            {!!form.attachments?.length && (
                <div className="flex flex-column gap-1 mt-2">
                    {form.attachments.map((url, index) => (
                        <div key={`${url}-${index}`} className="flex align-items-center gap-2">
                            <a href={url} target="_blank" rel="noopener noreferrer" className={`${TEXT_SMALL} text-primary flex-1 overflow-hidden text-overflow-ellipsis`}>
                                {url}
                            </a>
                            <Button
                                icon="pi pi-times"
                                text
                                severity="danger"
                                disabled={loading}
                                onClick={() =>
                                    setForm((prev) => ({
                                        ...prev,
                                        attachments: prev.attachments.filter((_, i) => i !== index),
                                    }))
                                }
                            />
                        </div>
                    ))}
                </div>
            )}

            <Button className="mt-4 w-full" label="Send Notification" severity="warning" loading={loading} onClick={save} />
        </Dialog>
    );
}
